import { useEffect, useRef, useState } from "react";
import type { Strings } from "../i18n/strings";
import { archetypeColor } from "../lib/archetypeMeta";
import { deriveDisplayStats } from "../lib/displayStats";
import { captureCard, saveCard, shareCard, saveVideo, shareVideo } from "../lib/exportCard";
import { renderCardVideo, videoRenderEnabled, type RenderedVideo } from "../lib/renderClient";
import { shareCaption, copyText } from "../lib/shareCaption";
import { track } from "../lib/analytics";
import type { Lang, VerdictResult } from "../lib/types";
import ResultCard, { type CardData } from "./ResultCard";
import { IconAgain, IconBack, IconSave, IconShare } from "./Icons";

// 영상 렌더 중 안내(언어별). 콜드스타트 포함 수~십수 초 걸릴 수 있어 연출이 덮는다.
const MAKING: Record<Lang, string> = {
  ko: "영상 만드는 중…",
  en: "Making video…",
  ja: "動画を作成中…",
};

interface Props {
  t: Strings;
  lang: Lang;
  result: VerdictResult;
  onReplay: () => void; // ↺ → 홈(새로 쓰기)
  onBack: () => void; // ← → 리포트
}

/** VerdictResult → 카드 데이터(원문 텍스트 제외). 서버 영상 렌더도 같은 데이터를 쓴다. */
export function toCardData(result: VerdictResult, lang: Lang): CardData {
  const st = deriveDisplayStats(result.signals);
  return {
    lang,
    archetype: result.archetype,
    score: result.score,
    headline: result.judgement.headline,
    curve: result.curve,
    words: st.words,
    exclaims: st.exclaims,
    charCount: result.signals.charCount,
  };
}

// ⑧ 공유 카드 (9:16). 광고는 이 카드에 '도달하기 전'(자랑하기 → 전면광고)에 이미 봤으므로
// 저장·공유·스크린샷은 무료. (export 시점에 걸면 스크린샷으로 우회되므로 보기 게이트로 옮김.)
export default function ShareCard({ t, lang, result, onReplay, onBack }: Props) {
  const { archetype } = result;
  const color = archetypeColor(archetype);
  const data = toCardData(result, lang);
  const stageRef = useRef<HTMLDivElement>(null);
  const videoPromise = useRef<Promise<RenderedVideo> | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<"" | "ok" | "fail">("");

  useEffect(() => {
    track("card_viewed", { archetype });
  }, [archetype]);

  // 영상 렌더 — '프로미스'를 캐시해 중복 렌더 방지(미리 렌더 + 클릭이 같은 렌더를 공유).
  function getVideo(): Promise<RenderedVideo> {
    if (!videoPromise.current) {
      videoPromise.current = renderCardVideo(data).catch((e) => {
        videoPromise.current = null; // 실패 시 캐시 비워 다음 시도 허용
        throw e;
      });
    }
    return videoPromise.current;
  }

  // ★카드 진입 = 공유 의도 → 영상을 미리 렌더해둔다.
  //   그래야 공유 클릭 시 영상이 이미 준비돼 navigator.share를 '제스처 안에서 즉시' 호출 →
  //   PC에서 첫 클릭에 바로 공유창(두 번 눌러야 뜨던 문제 해결) + 모바일에서도 대기 없이 공유.
  useEffect(() => {
    if (videoRenderEnabled) void getVideo().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function exportCard(mode: "save" | "share") {
    if (!stageRef.current || busy) return;
    setBusy(true);
    track(mode === "save" ? "card_saved" : "card_shared", { archetype });

    // 캡션(클립보드용). ★복사는 공유를 '먼저' 호출한 뒤에 한다 — clipboard 쓰기가 제스처 권한을
    //   먼저 소비하면 navigator.share가 거부될 수 있어서(웹). 공유가 제스처 우선.
    const caption = shareCaption(lang, result.score, archetype);

    // 1순위: 영상(mp4). ★렌더가 실패했을 때만 이미지로 폴백한다.
    //   (렌더 성공 후 공유/저장 '자체'가 실패해도 이미지로 다시 시도하지 않는다 → 공유가 두 번 뜨는 것 방지.)
    if (videoRenderEnabled) {
      let video: RenderedVideo | null = null;
      try {
        video = await getVideo();
      } catch {
        track("video_fallback", { archetype });
      }
      if (video) {
        try {
          if (mode === "save") {
            const ok = await saveVideo(video);
            setSaved(ok ? "ok" : "fail");
          } else {
            await shareVideo(video, caption);
          }
        } catch {
          if (mode === "save") setSaved("fail"); // 공유 실패는 조용히(사용자 취소 포함). 이미지로 중복 시도 안 함.
        }
        if (mode === "share") void copyText(caption); // 공유 호출 뒤 캡션 복사(best-effort)
        setBusy(false);
        return;
      }
    }

    // 폴백: 영상 '렌더'가 안 될 때만 정적 이미지(PNG).
    try {
      const dataUrl = await captureCard(stageRef.current);
      if (mode === "save") {
        const ok = await saveCard(dataUrl);
        setSaved(ok ? "ok" : "fail");
      } else {
        await shareCard(dataUrl, caption);
      }
    } catch {
      if (mode === "save") setSaved("fail");
    }
    if (mode === "share") void copyText(caption); // 공유 호출 뒤 캡션 복사(best-effort)
    setBusy(false); // 추출 후 카드에 그대로 머문다(홈으로 안 감)
  }

  return (
    <div className="screen share">
      <div className="share-header">
        <button className="navbtn icon-btn" onClick={onBack}>
          <IconBack size={17} color="rgba(255,255,255,.6)" /> {t.back}
        </button>
        <span className="card-label">{t.cardLabel}</span>
      </div>

      <ResultCard ref={stageRef} t={t} data={data} />

      {busy && videoRenderEnabled && <div className="render-status">{MAKING[lang]}</div>}

      <div className="card-actions">
        <button className="cardbtn cardbtn-sq" onClick={onReplay} aria-label="replay">
          <IconAgain size={19} />
        </button>
        <button className="cardbtn" onClick={() => exportCard("save")} disabled={busy}>
          <IconSave size={18} /> {t.save}
          {saved === "ok" ? " ✓" : saved === "fail" ? " ✕" : ""}
        </button>
        <button
          className="cardbtn cardbtn-solid"
          style={{ background: color, color: "#0a0a0a" }}
          onClick={() => exportCard("share")}
          disabled={busy}
        >
          <IconShare size={17} color="#0a0a0a" /> {t.share}
        </button>
      </div>
    </div>
  );
}
